import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, AgentDraftBadge, PrivacyNotice } from "../components/Brand";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Mail, CheckCircle2, Send } from "lucide-react";

const SUMMARY_LABELS = {
  work_completed: "Work completed", participants: "Participants / audience",
  instructional_focus: "Instructional focus", evidence_materials: "Evidence / materials",
  successes_observations: "Successes / observations", follow_up_actions: "Follow-up actions",
  internal_notes: "Internal notes (never shared)",
};

function DraftCard({ draft, onChange, isAdmin }) {
  const [d, setD] = useState(draft);
  const [busy, setBusy] = useState(false);
  const [blockers, setBlockers] = useState(null);
  const [newLink, setNewLink] = useState({ label: "", url: "" });
  const editable = ["draft", "approved"].includes(d.status);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/drafts/${d.id}`, { subject: d.subject, body: d.body, resource_links: d.resource_links || [] });
      setD(data); onChange();
      toast.success(draft.status === "approved" ? "Saved — draft reverted to 'draft' and needs re-approval before sending." : "Draft saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/drafts/${d.id}/approve`);
      onChange();
      toast.success("Draft approved — ready to send.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true);
    setBlockers(null);
    try {
      const { data } = await api.post(`/drafts/${d.id}/send`);
      const res = data.send_results || [];
      const failed = res.filter((r) => r.status !== "sent");
      if (res.length === 0) toast.success("Already sent to all recipients — no duplicates sent.");
      else if (failed.length === 0) toast.success(`Sent to ${res.length} recipient(s). Replies go to the consultant's reply-to address.`);
      else toast.error(`${res.length - failed.length} sent, ${failed.length} failed (${failed.map((f) => f.email).join(", ")}). Retry sends only the failures.`);
      onChange();
    } catch (e) {
      try { const { data } = await api.get(`/drafts/${d.id}/send-check`); setBlockers(data.blockers); } catch {}
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const addLink = () => {
    if (!newLink.label || !newLink.url) return;
    setD({ ...d, resource_links: [...(d.resource_links || []), { ...newLink }] });
    setNewLink({ label: "", url: "" });
  };

  return (
    <div className="card p-5 space-y-4" data-testid={`draft-card-${d.audience}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Mail size={16} className="text-terracotta" />
          <span className="font-bold text-navy capitalize">{d.audience} follow-up</span>
          {d.session_label && <span className="badge-neutral" data-testid={`draft-session-${d.id}`}>{d.session_label}</span>}
        </div>
        <div className="flex items-center gap-2">
          {d.status === "draft" && <AgentDraftBadge />}
          {d.status === "approved" && <StatusBadge status="approved" />}
          <StatusBadge status={d.sent_status} prefix="Sent: " />
        </div>
      </div>
      <div className="text-xs rounded-lg bg-sand px-3 py-2 text-[#57534E]" data-testid={`draft-recipients-${d.audience}`}>
        <b>To:</b> {d.recipient_names.map((n, i) => `${n} <${d.recipient_emails[i] || "no email"}>`).join(", ") || "—"}
      </div>
      {(d.sent_to || []).length > 0 && (
        <div className="text-xs rounded-lg border border-[#E7E1D4] px-3 py-2 space-y-1" data-testid={`draft-sent-log-${d.id}`}>
          {d.sent_to.map((s) => (
            <div key={s.email} className="flex flex-wrap items-center gap-2">
              <span className={s.status === "sent" ? "badge-approved" : "badge-nosend"}>{s.status}</span>
              <span className="text-[#57534E]">{s.name} &lt;{s.email}&gt;</span>
              {s.error && <span className="text-[#991B1B]">{s.error}</span>}
            </div>))}
        </div>)}
      {d.audience === "teachers" && (
        <div className="text-xs rounded-lg border px-3 py-2 flex flex-wrap items-center gap-2"
          data-testid={`draft-evallink-${d.id}`}
          style={{ borderColor: d.evaluation_link?.startsWith("http") && !d.evaluation_link.includes("UNCONFIGURED") ? "#6EE7B7" : "#FCA5A5",
                   background: d.evaluation_link?.startsWith("http") && !d.evaluation_link.includes("UNCONFIGURED") ? "#ECFDF5" : "#FEF2F2" }}>
          <b>Evaluation link:</b>
          {d.evaluation_link?.startsWith("http") && !d.evaluation_link.includes("UNCONFIGURED") ? (
            <>
              <span className="break-all font-mono">{d.evaluation_link}</span>
              <a href={d.evaluation_link} target="_blank" rel="noreferrer" className="font-bold text-terracotta hover:underline" data-testid={`draft-evallink-open-${d.id}`}>Open form</a>
            </>
          ) : (
            <span className="font-bold text-[#991B1B]">Missing/invalid — sending is blocked until this resolves.</span>
          )}
        </div>
      )}
      <div>
        <label className="label">Subject</label>
        <input className="input" value={d.subject} disabled={!editable} data-testid={`draft-subject-${d.audience}`}
          onChange={(e) => setD({ ...d, subject: e.target.value })} />
      </div>
      <div>
        <label className="label">Body</label>
        <textarea className="input min-h-[180px] leading-relaxed" value={d.body} disabled={!editable}
          data-testid={`draft-body-${d.audience}`} onChange={(e) => setD({ ...d, body: e.target.value })} />
      </div>
      <div>
        <label className="label">Attachments & resource links</label>
        {(d.resource_links || []).length > 0 && (
          <ul className="space-y-1 mb-2">
            {d.resource_links.map((l, i) => (
              <li key={i} className="text-xs flex items-center gap-2" data-testid={`draft-link-${d.id}-${i}`}>
                <span className="font-bold text-navy">{l.label}</span>
                <span className="text-[#78716C] break-all">{l.url}</span>
                {editable && <button className="text-[#991B1B] font-bold" data-testid={`draft-link-remove-${d.id}-${i}`}
                  onClick={() => setD({ ...d, resource_links: d.resource_links.filter((_, j) => j !== i) })}>×</button>}
              </li>
            ))}
          </ul>
        )}
        {editable && (
          <div className="flex flex-wrap gap-2">
            <input className="input !py-1.5 text-xs flex-1 min-w-[120px]" placeholder="Label" value={newLink.label}
              data-testid={`draft-link-label-${d.id}`} onChange={(e) => setNewLink({ ...newLink, label: e.target.value })} />
            <input className="input !py-1.5 text-xs flex-[2] min-w-[180px]" placeholder="https://…" value={newLink.url}
              data-testid={`draft-link-url-${d.id}`} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} />
            <button className="btn-ghost !py-1.5 text-xs" onClick={addLink} data-testid={`draft-link-add-${d.id}`}>Add link</button>
          </div>
        )}
      </div>
      {draft.status === "approved" && editable && (
        <p className="text-xs text-[#92400E]" data-testid={`draft-reapproval-note-${d.id}`}>This draft is approved — saving any edit reverts it to draft and requires your review again before sending.</p>
      )}
      {blockers && (
        <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3" data-testid={`draft-blockers-${d.id}`}>
          <div className="text-xs font-black text-[#991B1B] mb-1">Pre-send checklist — blocked:</div>
          <ul className="text-xs text-[#991B1B] list-disc ml-4 space-y-0.5">
            {blockers.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {editable && <button className="btn-outline" onClick={save} disabled={busy} data-testid={`draft-save-${d.audience}`}>Save edits</button>}
        {d.status === "draft" && <button className="btn-primary" onClick={approve} disabled={busy} data-testid={`draft-approve-${d.audience}`}>
          <CheckCircle2 size={15} /> Approve draft</button>}
        {d.status === "approved" && d.sent_status !== "sent" && (
          <button className="btn-primary" onClick={send} disabled={busy} data-testid={`draft-send-${d.audience}`}>
            <Send size={14} /> {busy ? "Sending…" : d.sent_status === "not_sent" ? "Send now" : "Retry failed sends"}</button>)}
        {d.sent_status === "sent" && <span className="badge-approved" data-testid={`draft-all-sent-${d.id}`}>All sent</span>}
      </div>
    </div>
  );
}

export default function LogDetail() {
  const { logId } = useParams();
  const { user } = useAuth();
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";
  const isOwner = log?.consultant_id === user?.id;

  const load = () => api.get(`/logs/${logId}`).then((r) => setLog(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [logId]);

  if (!log) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="log-detail-loading">Loading record…</div>;

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/logs/${logId}/${action}`);
      await load();
      toast.success(`Record ${action}d.`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const generateDrafts = async () => {
    setBusy(true);
    try {
      await api.post(`/logs/${logId}/generate-drafts`);
      await load();
      toast.success("Follow-up drafts created. All remain DRAFTS — nothing sends automatically.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="log-detail-page">
      <div className="fade-up">
        <Link to="/logs" className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1 mb-3" data-testid="back-to-logs-link">
          <ArrowLeft size={14} /> Daily Logs
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow">Daily service record</p>
            <h1 className="section-title text-3xl mt-1">{log.school?.name || "—"} · {log.date}</h1>
          </div>
          <StatusBadge status={log.status} />
        </div>
      </div>

      <PrivacyNotice />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 fade-up-1" data-testid="log-context-grid">
        {[["Organization", log.organization?.name], ["Services", (log.service_types || []).join(" · ")],
          ["Consultant", log.consultant_name], ["Engagement", log.engagement?.title]].map(([k, v]) => (
          <div key={k} className="card p-4">
            <div className="label !mb-1">{k}</div>
            <div className="text-sm font-bold text-navy">{v || "—"}</div>
          </div>
        ))}
      </div>

      {(log.sessions || []).length > 0 && (
        <div className="card p-5 fade-up-1" data-testid="log-sessions-card">
          <h2 className="font-serif font-bold text-lg text-navy mb-2">Sessions (one billable day total)</h2>
          <div className="flex flex-wrap gap-2">
            {log.sessions.map((s, i) => (
              <span key={i} className="badge-neutral" data-testid={`log-session-${i}`}>{s.label} · {s.participant_ids.length} participant{s.participant_ids.length === 1 ? "" : "s"}</span>
            ))}
          </div>
        </div>
      )}

      <div className="agent-panel p-6 space-y-4 fade-up-2" data-testid="log-summary-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-serif font-bold text-lg text-navy">Structured summary</h2>
          {log.status === "approved" ? <StatusBadge status="approved" prefix="Consultant/Admin approved" /> : <AgentDraftBadge />}
        </div>
        {log.agent_summary ? (
          <div className="space-y-3">
            {Object.entries(SUMMARY_LABELS).map(([k, label]) => (
              <div key={k} className="text-sm" data-testid={`summary-row-${k}`}>
                <span className={`font-bold ${k === "internal_notes" ? "text-[#991B1B]" : "text-navy"}`}>{label}: </span>
                <span className="text-[#57534E]">{log.agent_summary[k] || "—"}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#78716C]">No agent summary yet. <Link to={`/log-work/${log.id}`} className="font-bold text-terracotta hover:underline">Continue intake</Link></p>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 fade-up-3">
        <div className="card p-6" data-testid="original-note-card">
          <h2 className="font-serif font-bold text-lg text-navy mb-3">
            {log.input_mode === "voice" ? "Voice note transcript" : "Original note"}
          </h2>
          <p className="text-sm text-[#57534E] whitespace-pre-wrap leading-relaxed">
            {log.input_mode === "voice" ? (log.transcript || log.original_note || "—") : (log.original_note || "—")}
          </p>
          {log.audio_file && (
            <div className="mt-4">
              <div className="label">Private recording</div>
              <audio controls className="w-full" data-testid="audio-playback"
                src={`${process.env.REACT_APP_BACKEND_URL}/api/logs/${log.id}/audio`} />
              {isAdmin && (
                <button className="btn-ghost text-[#991B1B] mt-2" data-testid="delete-recording-button"
                  onClick={async () => { await api.delete(`/logs/${log.id}/audio`); load(); toast.success("Recording deleted per retention policy."); }}>
                  Delete recording (retention)
                </button>
              )}
            </div>
          )}
        </div>
        <div className="card p-6" data-testid="audit-history-card">
          <h2 className="font-serif font-bold text-lg text-navy mb-3">Activity history</h2>
          <ul className="space-y-3">
            {log.audit.map((a) => (
              <li key={a.id} className="text-sm flex gap-3" data-testid={`audit-entry-${a.id}`}>
                <div className="w-2 h-2 rounded-full bg-terracotta mt-1.5 shrink-0" />
                <div>
                  <span className="font-bold text-navy">{a.actor_name}</span>{" "}
                  <span className="text-[#57534E]">{a.action.replace(/_/g, " ")}</span>
                  <div className="text-xs text-[#78716C]">{new Date(a.timestamp).toLocaleString()}</div>
                </div>
              </li>
            ))}
            {log.audit.length === 0 && <li className="text-sm text-[#78716C]">No activity yet.</li>}
          </ul>
        </div>
      </div>

      <div className="card p-6 space-y-4 fade-up-3" data-testid="log-actions-card">
        <h2 className="font-serif font-bold text-lg text-navy">Actions</h2>
        <div className="flex flex-wrap gap-3">
          {log.status === "draft" && (isOwner || isAdmin) && (
            <Link to={`/log-work/${log.id}`} className="btn-outline" data-testid="continue-intake-button">Continue intake</Link>
          )}
          {log.status === "submitted" && isAdmin && (
            <button className="btn-outline" onClick={() => act("review")} disabled={busy} data-testid="mark-reviewed-button">Mark reviewed</button>
          )}
          {(log.status === "submitted" || log.status === "reviewed") && isAdmin && (
            <button className="btn-primary" onClick={() => act("approve")} disabled={busy} data-testid="approve-log-button">
              <CheckCircle2 size={15} /> Approve record</button>
          )}
          {["submitted", "reviewed", "approved"].includes(log.status) && log.drafts.length === 0 && (isOwner || isAdmin) && (
            <button className="btn-navy" onClick={generateDrafts} disabled={busy} data-testid="generate-drafts-button">
              <Sparkles size={15} /> {busy ? "Drafting…" : "Generate follow-up drafts"}
            </button>
          )}
        </div>
      </div>

      {log.drafts.length > 0 && (
        <div className="space-y-4 fade-up-3" data-testid="followup-drafts-section">
          <div className="flex items-center gap-2">
            <h2 className="font-serif font-bold text-xl text-navy">Follow-up drafts</h2>
            <span className="badge-neutral" data-testid="sending-active-note">Consultant-approved follow-ups send via the platform-verified sender</span>
          </div>
          {log.drafts.map((d) => <DraftCard key={d.id} draft={d} onChange={load} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}
